import SockJS from 'sockjs-client';
import { Client } from '@stomp/stompjs';

let stompClient = null;
let messageHandlers = new Set();
let subscriptions = [];
let isConnecting = false;

const getToken = () => {
  return localStorage.getItem('token');
};

const getUsername = () => {
  const token = getToken();
  if (!token) return null;
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.sub;
  } catch (e) {
    return null;
  }
};

const setupSubscriptions = (username) => {
  if (!stompClient) {
    console.log('⚠️ Cannot subscribe - no STOMP client');
    return;
  }

  // Check if client is actually connected
  if (!stompClient.connected) {
    console.log('⚠️ Cannot subscribe - STOMP client not connected');
    return;
  }

  console.log('Setting up subscriptions for:', username);
  
  // Clear existing subscriptions
  subscriptions.forEach(sub => {
    try {
      if (sub && typeof sub.unsubscribe === 'function') {
        sub.unsubscribe();
      }
    } catch (e) {
      // Ignore errors
    }
  });
  subscriptions = [];

  // Subscribe to user-specific direct messages queue
  try {
    const subscription1 = stompClient.subscribe(`/user/${username}/queue/direct`, (message) => {
      console.log('=== MESSAGE RECEIVED ON /user/{username}/queue/direct ===');
      console.log('Raw message:', message.body);
      try {
        const receivedMessage = JSON.parse(message.body);
        console.log('Parsed message:', receivedMessage);
        // Notify all handlers
        messageHandlers.forEach(handler => {
          try {
            handler(receivedMessage);
          } catch (e) {
            console.error('Error in message handler:', e);
          }
        });
      } catch (e) {
        console.error('Error parsing message:', e, message.body);
      }
    });
    subscriptions.push(subscription1);
    console.log('✅ Subscribed to:', `/user/${username}/queue/direct`);
  } catch (e) {
    console.error('Error subscribing to /user/{username}/queue/direct:', e);
  }
  
  // Also subscribe to direct queue path (fallback)
  try {
    const subscription2 = stompClient.subscribe(`/queue/direct`, (message) => {
      console.log('=== MESSAGE RECEIVED ON /queue/direct ===');
      console.log('Raw message:', message.body);
      try {
        const receivedMessage = JSON.parse(message.body);
        console.log('Parsed message:', receivedMessage);
        messageHandlers.forEach(handler => {
          try {
            handler(receivedMessage);
          } catch (e) {
            console.error('Error in message handler:', e);
          }
        });
      } catch (e) {
        console.error('Error parsing message:', e, message.body);
      }
    });
    subscriptions.push(subscription2);
    console.log('✅ Subscribed to:', `/queue/direct`);
  } catch (e) {
    console.error('Error subscribing to /queue/direct:', e);
  }

  // Subscribe to topic fallback - backend broadcasts to both patterns
  // /topic/direct/{sender}/{recipient} and /topic/direct/{recipient}/{sender}
  // We'll subscribe to a pattern that matches when this user is either sender or recipient
  // Since STOMP doesn't support wildcards, we need to subscribe to a specific pattern
  // The backend sends to both patterns, so we'll subscribe to a catch-all pattern
  // Actually, we can't use wildcards, so we'll need to subscribe dynamically per conversation
  // For now, let's subscribe to a general pattern that the backend can use
  // Note: This is a workaround until convertAndSendToUser works properly
};

export const initializeWebSocket = () => {
  // If already connected, just setup subscriptions
  if (stompClient && stompClient.connected) {
    console.log('WebSocket already connected, setting up subscriptions');
    const username = getUsername();
    if (username) {
      setupSubscriptions(username);
    }
    return stompClient;
  }

  // If already connecting, wait
  if (isConnecting) {
    console.log('WebSocket connection already in progress');
    return stompClient;
  }

  const token = getToken();
  if (!token) {
    console.error('No token available for WebSocket connection');
    return null;
  }

  const username = getUsername();
  if (!username) {
    console.error('Could not extract username from token');
    return null;
  }

  console.log('Initializing WebSocket connection for:', username);
  isConnecting = true;

  // Get WebSocket URL from environment variable (points to API Gateway)
  // SockJS automatically converts:
  //   http:// → ws:// (unencrypted WebSocket)
  //   https:// → wss:// (encrypted WebSocket)
  const getWebSocketUrl = () => {
    const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8080';
    // Remove trailing slash if present
    const baseUrl = apiUrl.replace(/\/$/, '');
    // WebSocket endpoint through API Gateway
    // SockJS will automatically use ws:// or wss:// based on http:// or https://
    const wsUrl = `${baseUrl}/ws`;
    console.log('🔌 WebSocket URL:', wsUrl);
    console.log('🔌 API URL from env:', apiUrl);
    return wsUrl;
  };

  const socket = new SockJS(getWebSocketUrl());
  stompClient = new Client({
    webSocketFactory: () => socket,
    reconnectDelay: 5000,
    heartbeatIncoming: 4000,
    heartbeatOutgoing: 4000,
    connectHeaders: {
      Authorization: `Bearer ${token}`
    },
    onConnect: (frame) => {
      console.log('✅ WebSocket Connected:', frame);
      console.log('Username:', username);
      isConnecting = false;
      
      // Wait for STOMP connection to be fully ready before subscribing
      setTimeout(() => {
        if (stompClient && stompClient.connected) {
          setupSubscriptions(username);
        } else {
          console.error('STOMP client not ready for subscription');
        }
      }, 500);
    },
    onStompError: (frame) => {
      console.error('❌ STOMP error:', frame);
      isConnecting = false;
    },
    onWebSocketClose: () => {
      console.log('WebSocket closed');
      isConnecting = false;
      subscriptions = [];
    },
    onDisconnect: () => {
      console.log('WebSocket disconnected');
      isConnecting = false;
      subscriptions = [];
    },
  });

  stompClient.activate();
  return stompClient;
};

export const addMessageHandler = (handler) => {
  messageHandlers.add(handler);
  console.log('Message handler added. Total handlers:', messageHandlers.size);
  
  return () => {
    messageHandlers.delete(handler);
    console.log('Message handler removed. Total handlers:', messageHandlers.size);
  };
};

export const removeMessageHandler = (handler) => {
  messageHandlers.delete(handler);
};

const doSendMessage = (recipientUsername, content) => {
  const username = getUsername();
  if (!username) {
    console.error('Could not get username');
    return;
  }
  
  const messagePayload = {
    sender: username,
    recipient: recipientUsername,
    content: content
  };
  
  console.log('📤 Publishing message:', messagePayload);
  console.log('Destination: /app/chat.direct');
  
  try {
    stompClient.publish({
      destination: '/app/chat.direct',
      body: JSON.stringify(messagePayload)
    });
    console.log('✅ Message published');
  } catch (e) {
    console.error('❌ Error publishing message:', e);
  }
};

export const sendDirectMessage = (recipientUsername, content) => {
  console.log('=== sendDirectMessage called ===');
  console.log('stompClient exists:', !!stompClient);
  console.log('stompClient.connected:', stompClient?.connected);
  
  if (!stompClient || !stompClient.connected) {
    console.log('⚠️ WebSocket not connected, initializing...');
    initializeWebSocket();
    // Wait for connection with retries
    let retries = 0;
    const trySend = setInterval(() => {
      retries++;
      if (stompClient && stompClient.connected) {
        clearInterval(trySend);
        doSendMessage(recipientUsername, content);
      } else if (retries > 10) {
        clearInterval(trySend);
        console.error('❌ Failed to connect WebSocket after retries');
      }
    }, 200);
  } else {
    doSendMessage(recipientUsername, content);
  }
};

export const sendRoomMessage = (roomId, content) => {
  if (stompClient && stompClient.connected) {
    const username = getUsername();
    if (!username) {
      console.error('Could not get username');
      return;
    }
    
    stompClient.publish({
      destination: '/app/chat.room',
      body: JSON.stringify({
        sender: username,
        roomId: roomId,
        content: content
      })
    });
  } else {
    console.error('WebSocket not connected');
  }
};

export const subscribeToRoom = (roomId, onMessageReceived) => {
  if (stompClient && stompClient.connected) {
    return stompClient.subscribe(`/topic/room/${roomId}`, (message) => {
      const receivedMessage = JSON.parse(message.body);
      onMessageReceived(receivedMessage);
    });
  }
  return null;
};

export const isConnected = () => {
  return stompClient && stompClient.connected;
};

export const subscribeToTopic = (topic, handler) => {
  if (!stompClient || !stompClient.connected) {
    console.error('Cannot subscribe to topic - WebSocket not connected');
    return null;
  }

  try {
    const subscription = stompClient.subscribe(topic, (message) => {
      console.log(`=== MESSAGE RECEIVED ON TOPIC: ${topic} ===`);
      console.log('Raw message:', message.body);
      try {
        const receivedMessage = JSON.parse(message.body);
        console.log('Parsed message:', receivedMessage);
        handler(receivedMessage);
      } catch (e) {
        console.error('Error parsing message:', e, message.body);
      }
    });
    console.log(`✅ Subscribed to topic: ${topic}`);
    return subscription;
  } catch (e) {
    console.error(`Error subscribing to topic ${topic}:`, e);
    return null;
  }
};

export const sendTypingIndicator = (recipientUsername, isTyping) => {
  if (!stompClient || !stompClient.connected) {
    console.error('Cannot send typing indicator - WebSocket not connected');
    return;
  }

  const username = getUsername();
  if (!username) {
    console.error('Could not get username');
    return;
  }

  try {
    stompClient.publish({
      destination: '/app/chat.typing',
      body: JSON.stringify({
        sender: username,
        recipient: recipientUsername,
        isTyping: isTyping
      })
    });
  } catch (e) {
    console.error('Error sending typing indicator:', e);
  }
};

export const sendDeliveryConfirmation = (messageId, recipientUsername) => {
  if (!stompClient || !stompClient.connected) {
    console.error('Cannot send delivery confirmation - WebSocket not connected');
    return;
  }

  try {
    stompClient.publish({
      destination: '/app/chat.delivered',
      body: JSON.stringify({
        messageId: messageId,
        recipient: recipientUsername
      })
    });
  } catch (e) {
    console.error('Error sending delivery confirmation:', e);
  }
};

export const sendReadReceipt = (otherUser) => {
  if (!stompClient || !stompClient.connected) {
    console.error('Cannot send read receipt - WebSocket not connected');
    return;
  }

  const username = getUsername();
  if (!username) {
    console.error('Could not get username');
    return;
  }

  try {
    stompClient.publish({
      destination: '/app/chat.read',
      body: JSON.stringify({
        currentUser: username,
        otherUser: otherUser
      })
    });
  } catch (e) {
    console.error('Error sending read receipt:', e);
  }
};

export const subscribeToTypingIndicator = (otherUser, handler) => {
  if (!stompClient || !stompClient.connected) {
    return null;
  }

  const username = getUsername();
  if (!username) return null;

  const [user1, user2] = [username, otherUser].sort();
  const topic = `/topic/typing/${user1}/${user2}`;

  try {
    return stompClient.subscribe(topic, (message) => {
      try {
        const event = JSON.parse(message.body);
        handler(event);
      } catch (e) {
        console.error('Error parsing typing indicator:', e);
      }
    });
  } catch (e) {
    console.error('Error subscribing to typing indicator:', e);
    return null;
  }
};

export const subscribeToDeliveryStatus = (otherUser, handler) => {
  if (!stompClient || !stompClient.connected) {
    return null;
  }

  const username = getUsername();
  if (!username) return null;

  const [user1, user2] = [username, otherUser].sort();
  const topic = `/topic/delivery/${user1}/${user2}`;

  try {
    return stompClient.subscribe(topic, (message) => {
      try {
        const event = JSON.parse(message.body);
        handler(event);
      } catch (e) {
        console.error('Error parsing delivery status:', e);
      }
    });
  } catch (e) {
    console.error('Error subscribing to delivery status:', e);
    return null;
  }
};

export const subscribeToReadReceipts = (otherUser, handler) => {
  if (!stompClient || !stompClient.connected) {
    return null;
  }

  const username = getUsername();
  if (!username) return null;

  const [user1, user2] = [username, otherUser].sort();
  const topic = `/topic/read/${user1}/${user2}`;

  try {
    return stompClient.subscribe(topic, (message) => {
      try {
        const event = JSON.parse(message.body);
        handler(event);
      } catch (e) {
        console.error('Error parsing read receipt:', e);
      }
    });
  } catch (e) {
    console.error('Error subscribing to read receipts:', e);
    return null;
  }
};

export const getWebSocketManager = () => {
  return {
    isConnected: () => isConnected(),
    subscribeToTopic: subscribeToTopic
  };
};

export const disconnectWebSocket = () => {
  if (stompClient) {
    subscriptions.forEach(sub => {
      try {
        if (sub && typeof sub.unsubscribe === 'function') {
          sub.unsubscribe();
        }
      } catch (e) {
        // Ignore
      }
    });
    subscriptions = [];
    messageHandlers.clear();
    stompClient.deactivate();
    stompClient = null;
    isConnecting = false;
    console.log('WebSocket disconnected');
  }
};
